import type { ControllerMode } from "@/data/types";

/**
 * Operator-facing catalog of dispatch modes. Description text matches the
 * behaviour implemented by the EMS server's controller-service (see
 * EMS-Admin/api/.../controller_schedule.py and the SCADA documentation
 * for the parameter schemas).
 *
 * `params` describes the form fields each mode exposes. The shape mirrors
 * the controller-service params dict so an entry saved here is directly
 * consumable by the edge controller without a translation layer.
 */
export interface ModeParam {
  key: string;
  label: string;
  hint: string;
  unit: string;
  /** Default when an entry is first created in this mode. */
  default: number;
  /** Allow negative values? (ConstantPower can be signed.) */
  signed?: boolean;
  /** Minimum value the form will accept (clamped on save). */
  min?: number;
}

export interface ModeInfo {
  /** Short operator-friendly label (used everywhere the mode is shown). */
  label: string;
  /** One-sentence summary — appears under the mode chip in the picker. */
  summary: string;
  /** Two-to-three sentence longer description for the "tell me more"
   *  tooltip / drawer. Plain prose; describes intent + behaviour. */
  description: string;
  /** Parameter form schema, if the mode takes any. */
  params: ModeParam[];
}

export const MODE_CATALOG: Record<ControllerMode, ModeInfo> = {
  gridZeroControl: {
    label: "Grid Zero",
    summary: "Hold the grid coupling at 0 kW — self-consume everything on site.",
    description:
      "The controller dispatches the battery to cancel out the building's net grid power. " +
      "When the load exceeds PV the battery discharges to fill the gap; when PV overproduces the battery soaks up the surplus. " +
      "No parameters required — the inverter follows the grid meter via a PID loop with the setpoint pinned at 0 kW.",
    params: [],
  },
  PeakShaving: {
    label: "Peak Shaving",
    summary: "Cap grid import and export within an operator-defined corridor.",
    description:
      "The battery is allowed to dispatch only when grid power would exceed the configured caps. " +
      "Below the import cap the controller stays passive; above it, the battery discharges to bring grid power back inside the corridor. " +
      "Same logic for export with the export cap. Both caps are positive watts; the internal corridor is [−export_max, +import_max]. " +
      "Set both to 0 and it behaves identically to Grid Zero.",
    params: [
      {
        key: "import_max_W",
        label: "Import cap",
        hint: "Maximum allowed grid import. Battery discharges if site demand would exceed this.",
        unit: "W",
        default: 80_000,
        min: 0,
      },
      {
        key: "export_max_W",
        label: "Export cap",
        hint: "Maximum allowed grid export. Battery charges if PV surplus would exceed this.",
        unit: "W",
        default: 80_000,
        min: 0,
      },
    ],
  },
  ConstantPower: {
    label: "Constant Power",
    summary: "Dispatch the battery at a fixed power — open-loop, ignoring grid state.",
    description:
      "Sends a constant power command to the battery: positive = charging from the bus, negative = discharging to the bus. " +
      "Open-loop dispatch — the controller does not regulate grid power in this mode. " +
      "Values exceeding the BESS hardware bounds (max_pos_active_power / max_neg_active_power) are clamped at runtime. " +
      "Useful for capacity testing, ancillary-service participation, or schedule-driven energy arbitrage.",
    params: [
      {
        key: "power_W",
        label: "Power",
        hint: "Signed: positive = charge into battery, negative = discharge from battery.",
        unit: "W",
        default: 0,
        signed: true,
      },
    ],
  },
  OFF: {
    label: "OFF",
    summary: "Battery commanded off — DC relays open, no AC dispatch.",
    description:
      "The controller sends BESS_ONOFF = 0 and opens the DC relays. " +
      "The battery does not respond to grid conditions and consumes only standby power. " +
      "Use during commissioning, maintenance, or as a safety fallback in alarm conditions.",
    params: [],
  },
};

/**
 * Brand-aware mode tone — used to colour mode chips consistently across
 * the schedule grid, entry rows, override panel and any other place a
 * mode is rendered. Matches the SoC + accent palette.
 */
export const MODE_TONE: Record<ControllerMode, string> = {
  gridZeroControl: "bg-accent/15 text-accent border-accent/40",
  PeakShaving:     "bg-warn/15 text-warn border-warn/40",
  ConstantPower:   "bg-charge/15 text-charge border-charge/40",
  OFF:             "bg-stale/15 text-stale border-stale/40",
};

/** Modes the operator can pick from in any UI element. Source of truth
 *  for the entire SPA — don't hardcode mode arrays elsewhere. */
export const ORDERED_MODES: ControllerMode[] = [
  "gridZeroControl",
  "PeakShaving",
  "ConstantPower",
  "OFF",
];

/** Default params for a mode (used when an entry's mode is changed). */
export function defaultParamsFor(mode: ControllerMode): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of MODE_CATALOG[mode].params) out[p.key] = p.default;
  return out;
}
